import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SEO } from "../components/SEO";
import DesignCanvas from "./DesignCanvas";
import {
  Upload, ShoppingCart, FileText, Save, RotateCcw, X,
  ChevronLeft, Minus, Plus, ZoomIn, ZoomOut,
  Shirt, Flag, Palette, MessageSquare, Send, Scissors, Ruler, Sticker,
  Share2, Copy, Check, Sparkles, Loader2,
} from "lucide-react";
import { API, GARMENT_TYPES } from "../constants";

/* ═══════════ CONFIG ═══════════ */

const COLORS = {
  White: "#FFFFFF", Black: "#1a1a1a", Navy: "#1e3a5f", Red: "#c0392b",
  "Royal Blue": "#2980b9", Forest: "#27ae60", Charcoal: "#444444",
  Gray: "#95a5a6", Orange: "#e67e22", Maroon: "#7b241c", Purple: "#6c3483",
  Gold: "#d4ac0d", Pink: "#e91e8a",
};

const PLACEMENTS = [
  { id: "front", label: "Front", x: 50, y: 42, w: 42, h: 38 },
  { id: "back", label: "Back", x: 50, y: 42, w: 42, h: 38 },
  { id: "left_side", label: "Left Side", x: 50, y: 38, w: 36, h: 38 },
  { id: "right_side", label: "Right Side", x: 50, y: 38, w: 36, h: 38 },
];

const LF_PLACEMENTS = [
  { id: "center", label: "Centered", x: 50, y: 50, w: 70, h: 60 },
  { id: "full_bleed", label: "Full Bleed", x: 50, y: 50, w: 90, h: 85 },
];

const SHAPES = {
  tshirt: "M 25,18 Q 22,18 15,22 L 8,35 L 18,38 L 22,28 L 22,78 Q 22,82 28,82 L 72,82 Q 78,82 78,78 L 78,28 L 82,38 L 92,35 L 85,22 Q 78,18 75,18 Q 70,18 62,16 Q 56,14 50,14 Q 44,14 38,16 Q 30,18 25,18 Z",
  hoodie: "M 22,20 Q 18,20 10,24 L 2,40 L 14,44 L 18,30 L 18,82 Q 18,86 24,86 L 76,86 Q 82,86 82,82 L 82,30 L 86,44 L 98,40 L 90,24 Q 82,20 78,20 Q 72,20 64,18 L 62,12 Q 56,8 50,8 Q 44,8 38,12 L 36,18 Q 28,20 22,20 Z",
  tank: "M 30,16 L 28,18 L 22,78 Q 22,82 28,82 L 72,82 Q 78,82 78,78 L 72,18 L 70,16 Q 64,14 58,12 Q 54,11 50,11 Q 46,11 42,12 Q 36,14 30,16 Z",
  polo: "M 25,18 Q 22,18 15,22 L 8,35 L 18,38 L 22,28 L 22,78 Q 22,82 28,82 L 72,82 Q 78,82 78,78 L 78,28 L 82,38 L 92,35 L 85,22 Q 78,18 75,18 Q 70,18 62,16 Q 56,14 50,14 Q 44,14 38,16 Q 30,18 25,18 Z",
  hat: "M 15,55 Q 15,35 30,25 Q 40,18 50,18 Q 60,18 70,25 Q 85,35 85,55 L 85,60 Q 50,62 15,60 Z",
};

const PRINT_METHODS = [
  { id: "dtf_printing", label: "DTF Printing", desc: "Full color, any image" },
  { id: "screen_printing", label: "Screen Printing", desc: "Best for bulk, 1-12 colors" },
  { id: "embroidery", label: "Embroidery", desc: "Premium, stitched finish" },
];

const STICKER_CUT_TYPES = [
  { id: "kiss_cut", label: "Kiss Cut", desc: "Peels from backing", icon: "M 10,20 Q 10,10 20,10 L 80,10 Q 90,10 90,20 L 90,80 Q 90,90 80,90 L 20,90 Q 10,90 10,80 Z" },
  { id: "die_cut", label: "Die Cut", desc: "Cut to exact shape", icon: "M 50,8 L 62,35 L 92,38 L 70,58 L 78,88 L 50,72 L 22,88 L 30,58 L 8,38 L 38,35 Z" },
];

const STICKER_SHAPES = [
  { id: "custom", label: "Custom Shape", desc: "Any shape" },
  { id: "round", label: "Round / Circle", desc: "Perfect circle" },
  { id: "square", label: "Square", desc: "Rounded corners" },
  { id: "oval", label: "Oval", desc: "Ellipse shape" },
];

/* ═══════════ MAIN ═══════════ */

const DesignStudio = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);

  // Category
  const [category, setCategory] = useState(searchParams.get("cat") || "apparel");

  // Apparel
  const [garment, setGarment] = useState(searchParams.get("garment") || "tshirt");
  const [color, setColor] = useState("White");
  const [placement, setPlacement] = useState("front");
  const [printMethod, setPrintMethod] = useState(searchParams.get("method") || "dtf_printing");
  const [size, setSize] = useState("M");
  const [screenColors, setScreenColors] = useState(1);
  const [rushTier, setRushTier] = useState("standard");
  const [sizeBreakdown, setSizeBreakdown] = useState([{ size: "M", qty: 24 }]);

  // Embroidery
  const [embWidth, setEmbWidth] = useState(3);
  const [embHeight, setEmbHeight] = useState(2.5);
  const [embEstimate, setEmbEstimate] = useState(null);

  // Stickers
  const [stickerWidth, setStickerWidth] = useState(3);
  const [stickerHeight, setStickerHeight] = useState(3);
  const [stickerCutType, setStickerCutType] = useState("kiss_cut");
  const [stickerShape, setStickerShape] = useState("custom");
  const [stickerPrice, setStickerPrice] = useState(null);

  // Large format
  const [lfProducts, setLfProducts] = useState([]);
  const [lfSelected, setLfSelected] = useState(null);
  const [lfPlacement, setLfPlacement] = useState("center");
  const [lfSize, setLfSize] = useState("");
  const [lfOptions, setLfOptions] = useState({});

  // Shared
  const [artwork, setArtwork] = useState(null);
  const [artworkPreview, setArtworkPreview] = useState(null);
  const [artworkScale, setArtworkScale] = useState(100);
  const [quantity, setQuantity] = useState(24);
  const [notes, setNotes] = useState("");
  const [designNumber, setDesignNumber] = useState(null);
  const [saved, setSaved] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [exitForm, setExitForm] = useState({ name: "", email: "", phone: "" });
  const [price, setPrice] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const [proofId, setProofId] = useState(null);
  const [proofCopied, setProofCopied] = useState(false);
  const [creatingProof, setCreatingProof] = useState(false);
  const [enhancing, setEnhancing] = useState(false);
  const [artworkQuality, setArtworkQuality] = useState(null);

  const gInfo = GARMENT_TYPES[garment] || GARMENT_TYPES.tshirt;

  // Sync total quantity from size breakdown
  useEffect(() => {
    if (category === "apparel") {
      const total = sizeBreakdown.reduce((sum, row) => sum + (row.qty || 0), 0);
      if (total !== quantity && total > 0) setQuantity(total);
    }
  }, [sizeBreakdown, category]); // eslint-disable-line

  const pl = category === "apparel"
    ? (PLACEMENTS.find(p => p.id === placement) || PLACEMENTS[0])
    : (LF_PLACEMENTS.find(p => p.id === lfPlacement) || LF_PLACEMENTS[0]);

  const colorHex = COLORS[color] || "#FFFFFF";
  const isDark = ["Black", "Navy", "Charcoal", "Forest", "Maroon", "Purple"].includes(color);

  /* ── Fetch large format ── */
  useEffect(() => {
    axios.get(`${API}/designs/products/large-format`).then(r => {
      setLfProducts(r.data.products || []);
      if (r.data.products?.length) { setLfSelected(r.data.products[0]); setLfSize(r.data.products[0].sizes?.[0] || ""); }
    }).catch(() => {});
  }, []); // eslint-disable-line

  /* ── Calculate price (apparel) ── */
  const fetchPrice = useCallback(async () => {
    if (category === "large_format") return;
    try {
      if (category === "stickers") {
        const params = new URLSearchParams({
          product_id: "stickers", quantity: quantity.toString(),
          sticker_width: stickerWidth.toString(), sticker_height: stickerHeight.toString(),
          cut_type: stickerCutType,
        });
        const { data } = await axios.post(`${API}/calculate-price?${params.toString()}`);
        setStickerPrice(data);
        setPrice(data);
        return;
      }
      if (category !== "apparel") return;
      const params = new URLSearchParams({
        product_id: printMethod, quantity: quantity.toString(), garment_type: garment,
      });
      if (printMethod !== "embroidery") params.append("print_locations", placement);
      if (printMethod === "screen_printing") params.append("colors", screenColors.toString());
      if (printMethod === "embroidery") {
        const area = embWidth * embHeight;
        params.append("embroidery_size_inches", area.toString());
      }
      if (rushTier !== "standard") params.append("rush_tier", rushTier);
      const { data } = await axios.post(`${API}/calculate-price?${params.toString()}`);
      setPrice(data);
    } catch { setPrice(null); setStickerPrice(null); }
  }, [category, printMethod, quantity, garment, placement, screenColors, embWidth, embHeight, stickerWidth, stickerHeight, stickerCutType, rushTier]);

  useEffect(() => { fetchPrice(); }, [fetchPrice]);

  /* ── Embroidery estimate ── */
  useEffect(() => {
    if (printMethod !== "embroidery" || embWidth <= 0 || embHeight <= 0) { setEmbEstimate(null); return; }
    axios.post(`${API}/designs/estimate-embroidery`, {
      width_inches: embWidth, height_inches: embHeight, coverage: 0.65,
    }).then(r => setEmbEstimate(r.data)).catch(() => {});
  }, [printMethod, embWidth, embHeight]);

  /* ── File upload ── */
  const handleUpload = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please upload an image"); return; }
    setArtwork(f);
    const reader = new FileReader();
    reader.onload = (ev) => setArtworkPreview(ev.target.result);
    reader.readAsDataURL(f);
    setSaved(false);
    setArtworkQuality(null);

    // Check quality via upload endpoint
    const formData = new FormData();
    formData.append("file", f);
    axios.post(`${API}/upload-artwork`, formData).then(({ data }) => {
      setArtworkQuality(data.quality_analysis);
    }).catch(() => {});
  };

  const handleEnhanceArtwork = async () => {
    if (!artwork) return;
    setEnhancing(true);
    try {
      const formData = new FormData();
      formData.append("file", artwork);
      const { data } = await axios.post(`${API}/enhance-artwork`, formData, { timeout: 120000 });
      if (data.image_base64) {
        setArtworkPreview(data.image_base64);
        setArtworkQuality({ status: "excellent", message: "AI-enhanced — print ready!" });
        toast.success("Artwork enhanced! Ready for high-quality printing.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Enhancement failed. Try again.");
    } finally {
      setEnhancing(false);
    }
  };

  /* ── Exit intent ── */
  useEffect(() => {
    const handleMouse = (e) => {
      if (e.clientY <= 0 && artworkPreview && !saved && !designNumber) setShowExitModal(true);
    };
    const handleVis = () => {
      if (document.visibilityState === "hidden" && artworkPreview && !saved && !designNumber) setShowExitModal(true);
    };
    document.addEventListener("mouseleave", handleMouse);
    document.addEventListener("visibilitychange", handleVis);
    return () => { document.removeEventListener("mouseleave", handleMouse); document.removeEventListener("visibilitychange", handleVis); };
  }, [artworkPreview, saved, designNumber]);

  /* ── Save ── */
  const saveDesign = async () => {
    const isLF = category === "large_format";
    const isSticker = category === "stickers";
    try {
      const { data } = await axios.post(`${API}/designs`, {
        product_id: isSticker ? "stickers" : (isLF ? lfSelected?.id : printMethod),
        product_name: isSticker ? `Custom Stickers — ${STICKER_CUT_TYPES.find(c => c.id === stickerCutType)?.label || stickerCutType}` : (isLF ? lfSelected?.name : `${gInfo.name} — ${printMethod.replace(/_/g, " ")}`),
        garment_type: (isLF || isSticker) ? "" : garment,
        garment_color: (isLF || isSticker) ? "" : color,
        size: isSticker ? `${stickerWidth}" x ${stickerHeight}"` : (isLF ? lfSize : size),
        placement: isSticker ? stickerShape : (isLF ? lfPlacement : placement),
        artwork_url: artworkPreview || "",
        artwork_position: { scale: artworkScale, placement: pl },
        quantity,
        notes,
        print_method: (isLF || isSticker) ? "" : printMethod,
        embroidery_width: printMethod === "embroidery" ? embWidth : 0,
        embroidery_height: printMethod === "embroidery" ? embHeight : 0,
        options: isSticker ? { category: "stickers", cut_type: stickerCutType, shape: stickerShape, width: stickerWidth, height: stickerHeight } : (isLF ? { category: "large_format", product_id: lfSelected?.id, options: lfOptions } : { print_method: printMethod }),
      });
      setDesignNumber(data.design_number);
      setSaved(true);
      toast.success(`Design saved! #${data.design_number}`);
      return data.design_number;
    } catch { toast.error("Failed to save"); return null; }
  };

  const handleExitCapture = async () => {
    if (!exitForm.email) { toast.error("Email required"); return; }
    let dn = designNumber;
    if (!dn) dn = await saveDesign();
    if (!dn) return;
    try {
      await axios.post(`${API}/designs/exit-intent`, { design_number: dn, ...exitForm });
      setShowExitModal(false);
      toast.success("Saved! We'll follow up.");
    } catch { toast.error("Failed"); }
  };

  const handleBuyNow = async () => {
    let dn = designNumber;
    if (!dn) dn = await saveDesign();
    if (!dn) return;
    const isLF = category === "large_format";
    const isSticker = category === "stickers";
    const cart = JSON.parse(localStorage.getItem("cart") || "[]");
    cart.push({
      id: `ds-${Date.now()}`,
      product_id: isSticker ? "stickers" : (isLF ? lfSelected?.id : printMethod),
      product_name: isSticker ? `Custom Stickers — ${STICKER_CUT_TYPES.find(c => c.id === stickerCutType)?.label}` : (isLF ? lfSelected?.name : `${gInfo.name} — ${PRINT_METHODS.find(m => m.id === printMethod)?.label}`),
      quantity,
      price: isSticker ? (stickerPrice?.unit_price || 0) : (isLF ? (lfSelected?.base_price || 0) : (price?.unit_price || gInfo.price)),
      unit_price: isSticker ? (stickerPrice?.unit_price || 0) : (isLF ? (lfSelected?.base_price || 0) : (price?.unit_price || gInfo.price)),
      totalPrice: isSticker ? (stickerPrice?.total_price || 0) : (isLF ? (lfSelected?.base_price * quantity) : (price?.total_price || 0)),
      garment_type: (isLF || isSticker) ? "" : garment, garment_color: (isLF || isSticker) ? "" : color,
      size: isSticker ? `${stickerWidth}" x ${stickerHeight}"` : (isLF ? lfSize : size),
      placement: isSticker ? stickerShape : (isLF ? lfPlacement : placement),
      design_number: dn,
      ...(isSticker && { sticker_width: stickerWidth, sticker_height: stickerHeight, cut_type: stickerCutType }),
    });
    localStorage.setItem("cart", JSON.stringify(cart));
    toast.success("Added to cart!");
    navigate("/cart");
  };

  const handleQuote = async () => {
    let dn = designNumber;
    if (!dn) dn = await saveDesign();
    navigate(`/concierge-quote?design=${dn}`);
  };

  const handleShareProof = async () => {
    setCreatingProof(true);
    try {
      let dn = designNumber;
      if (!dn) dn = await saveDesign();
      if (!dn) { setCreatingProof(false); return; }
      const { data } = await axios.post(`${API}/designs/${dn}/proof`, {
        design_number: dn,
      });
      const pid = data.proof_id;
      setProofId(pid);
      const proofUrl = `${window.location.origin}/proof/${pid}`;
      await navigator.clipboard.writeText(proofUrl);
      setProofCopied(true);
      toast.success(`Proof link copied! ${pid}`);
      setTimeout(() => setProofCopied(false), 3000);
    } catch (err) {
      toast.error("Failed to create proof link");
    } finally {
      setCreatingProof(false);
    }
  };

  /* ── Price display ── */
  const priceLabel = () => {
    if (category === "stickers" && stickerPrice) {
      return { total: `$${stickerPrice.total_price?.toFixed(2)}`, per: `$${stickerPrice.unit_price?.toFixed(2)}/ea` };
    }
    if (category === "apparel" && price) {
      return { total: `$${price.total_price?.toFixed(2)}`, per: `$${price.unit_price?.toFixed(2)}/ea` };
    }
    if (category === "large_format" && lfSelected) {
      return { total: `$${lfSelected.base_price.toFixed(2)}`, per: lfSelected.price_unit };
    }
    return { total: "—", per: "" };
  };
  const priceInfo = priceLabel();

  /* ═══════════ RENDER ═══════════ */
  return (
    <div className="min-h-screen bg-paper" data-testid="design-studio">
      <SEO title="Design Studio" description="Design your custom apparel and signage" path="/design-studio" />

      {/* ── Top bar ── */}
      <div className="bg-bone border-b-2 border-ink sticky top-0 z-30 px-4 py-2 flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="flex items-center text-muted hover:text-ink font-mono text-[11px] uppercase tracking-[0.1em]" data-testid="back-btn">
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        <div className="text-center flex-1">
          <h1 className="font-archivo text-sm uppercase tracking-tight text-ink leading-none">Design Studio</h1>
          {designNumber && <p className="font-mono text-[10px] text-signal font-bold">{designNumber}</p>}
        </div>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={() => setShowChat(!showChat)} className="text-xs text-muted border-2 border-ink h-7 w-7 p-0 rounded-none" data-testid="chat-toggle">
            <MessageSquare className="w-4 h-4" />
          </Button>
          <Button size="sm" onClick={saveDesign} className="font-mono text-[10px] uppercase tracking-wider h-7 px-3 bg-ink text-acid border-2 border-ink rounded-none hover:bg-signal hover:text-bone" data-testid="save-design-btn">
            <Save className="w-3 h-3 mr-1" /> Save
          </Button>
        </div>
      </div>

      {/* ── Category tabs ── */}
      <div className="max-w-6xl mx-auto px-3 pt-3">
        <div className="flex border-2 border-ink max-w-md mx-auto" data-testid="category-switcher">
          {[
            { id: "apparel", icon: Shirt, label: "Apparel" },
            { id: "stickers", icon: Sticker, label: "Stickers" },
            { id: "large_format", icon: Flag, label: "Signs & Banners" },
          ].map((c, i) => (
            <button key={c.id} onClick={() => { setCategory(c.id); if (c.id === "stickers" && quantity < 25) setQuantity(50); else if (c.id === "apparel" && quantity < 1) setQuantity(24); }}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 font-mono text-[11px] uppercase tracking-[0.08em] font-bold transition ${i > 0 ? "border-l-2 border-ink" : ""} ${category === c.id ? "bg-ink text-acid" : "bg-bone text-ink hover:bg-stock"}`}
              data-testid={`cat-${c.id}`}>
              <c.icon className="w-3.5 h-3.5" /> {c.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Main layout ── */}
      <div className="max-w-6xl mx-auto px-3 py-3">
        <div className="grid lg:grid-cols-12 gap-3">

          {/* ══ PREVIEW (compact) ══ */}
          <div className="lg:col-span-5 order-1">
            <div className="border-2 border-ink bg-bone overflow-hidden" style={{ boxShadow: "6px 6px 0px 0px #0A0A0A" }}>
              <div className="p-3">
                {category === "apparel" ? (
                  <div data-testid="garment-preview">
                    <DesignCanvas
                      garmentColor={colorHex}
                      garmentType={garment}
                      activeSide={placement}
                      onSideChange={setPlacement}
                      artworkUrl={artworkPreview}
                      onDesignChange={(json) => { /* future: persist design state */ }}
                    />
                  </div>
                ) : category === "stickers" ? (
                  <div className="bg-stock border-2 border-ink flex items-center justify-center relative overflow-hidden" style={{ height: "clamp(180px, 28vh, 280px)" }} data-testid="sticker-preview">
                    {/* Subtle grid pattern for backing */}
                    <svg viewBox="0 0 120 120" className="h-full max-h-[260px]" style={{ filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.18))" }}>
                      <defs>
                        {/* Clip paths for each shape */}
                        <clipPath id="clip-round"><circle cx="60" cy="58" r="34" /></clipPath>
                        <clipPath id="clip-oval"><ellipse cx="60" cy="58" rx="40" ry="28" /></clipPath>
                        <clipPath id="clip-square"><rect x="20" y="20" width="80" height="76" rx="6" /></clipPath>
                        <clipPath id="clip-custom"><rect x="16" y="20" width="88" height="76" rx="4" /></clipPath>
                        {/* Contour clip — slightly smaller for artwork inset */}
                        <clipPath id="art-round"><circle cx="60" cy="58" r="30" /></clipPath>
                        <clipPath id="art-oval"><ellipse cx="60" cy="58" rx="36" ry="24" /></clipPath>
                        <clipPath id="art-square"><rect x="24" y="24" width="72" height="68" rx="4" /></clipPath>
                        <clipPath id="art-custom"><rect x="20" y="24" width="80" height="68" rx="3" /></clipPath>
                      </defs>

                      {/* KISS CUT: Show full backing sheet */}
                      {stickerCutType === "kiss_cut" && (
                        <>
                          {/* Backing paper with subtle texture */}
                          <rect x="6" y="6" width="108" height="108" rx="3" fill="#fafafa" stroke="#d4d4d4" strokeWidth="0.4" />
                          {/* Grid dots on backing */}
                          {[20,40,60,80,100].map(x => [20,40,60,80,100].map(y => (
                            <circle key={`${x}-${y}`} cx={x} cy={y} r="0.3" fill="#e5e5e5" />
                          )))}
                        </>
                      )}

                      {/* Sticker white base — the actual sticker material */}
                      {stickerShape === "round" ? (
                        <>
                          <circle cx="60" cy="58" r="34" fill="#fff" />
                          {/* Subtle shadow under sticker (die cut only) */}
                          {stickerCutType === "die_cut" && <circle cx="61" cy="59" r="34" fill="rgba(0,0,0,0.05)" />}
                          <circle cx="60" cy="58" r="34" fill="#fff" />
                        </>
                      ) : stickerShape === "oval" ? (
                        <>
                          {stickerCutType === "die_cut" && <ellipse cx="61" cy="59" rx="40" ry="28" fill="rgba(0,0,0,0.05)" />}
                          <ellipse cx="60" cy="58" rx="40" ry="28" fill="#fff" />
                        </>
                      ) : stickerShape === "square" ? (
                        <>
                          {stickerCutType === "die_cut" && <rect x="21" y="21" width="80" height="76" rx="6" fill="rgba(0,0,0,0.05)" />}
                          <rect x="20" y="20" width="80" height="76" rx="6" fill="#fff" />
                        </>
                      ) : (
                        <>
                          {stickerCutType === "die_cut" && <rect x="17" y="21" width="88" height="76" rx="4" fill="rgba(0,0,0,0.05)" />}
                          <rect x="16" y="20" width="88" height="76" rx="4" fill="#fff" />
                        </>
                      )}

                      {/* Artwork clipped to shape */}
                      {artworkPreview && (
                        <image href={artworkPreview}
                          x={60 - (38*artworkScale/100)} y={58 - (34*artworkScale/100)}
                          width={76*artworkScale/100} height={68*artworkScale/100}
                          preserveAspectRatio="xMidYMid meet"
                          clipPath={`url(#art-${stickerShape})`} />
                      )}

                      {/* "Your Design" placeholder */}
                      {!artworkPreview && (
                        <text x="60" y="60" textAnchor="middle" fill="rgba(0,0,0,0.08)" fontSize="4" fontWeight="600">Your Design Here</text>
                      )}

                      {/* CONTOUR CUT LINE — the trace */}
                      {stickerShape === "round" ? (
                        <circle cx="60" cy="58" r="34" fill="none"
                          stroke={stickerCutType === "kiss_cut" ? "#ef4444" : "#f97316"}
                          strokeWidth={stickerCutType === "kiss_cut" ? "0.4" : "0.8"}
                          strokeDasharray={stickerCutType === "kiss_cut" ? "1.5 1" : "0"} />
                      ) : stickerShape === "oval" ? (
                        <ellipse cx="60" cy="58" rx="40" ry="28" fill="none"
                          stroke={stickerCutType === "kiss_cut" ? "#ef4444" : "#f97316"}
                          strokeWidth={stickerCutType === "kiss_cut" ? "0.4" : "0.8"}
                          strokeDasharray={stickerCutType === "kiss_cut" ? "1.5 1" : "0"} />
                      ) : stickerShape === "square" ? (
                        <rect x="20" y="20" width="80" height="76" rx="6" fill="none"
                          stroke={stickerCutType === "kiss_cut" ? "#ef4444" : "#f97316"}
                          strokeWidth={stickerCutType === "kiss_cut" ? "0.4" : "0.8"}
                          strokeDasharray={stickerCutType === "kiss_cut" ? "1.5 1" : "0"} />
                      ) : (
                        <rect x="16" y="20" width="88" height="76" rx="4" fill="none"
                          stroke={stickerCutType === "kiss_cut" ? "#ef4444" : "#f97316"}
                          strokeWidth={stickerCutType === "kiss_cut" ? "0.4" : "0.8"}
                          strokeDasharray={stickerCutType === "kiss_cut" ? "1.5 1" : "0"} />
                      )}

                      {/* Cut type label */}
                      <text x="60" y="108" textAnchor="middle" fill="#9ca3af" fontSize="2.8">
                        {stickerCutType === "kiss_cut" ? "Kiss Cut" : "Die Cut"} — {stickerWidth}" x {stickerHeight}"
                      </text>
                      {stickerCutType === "kiss_cut" && (
                        <text x="60" y="113" textAnchor="middle" fill="#d4d4d4" fontSize="2">
                          Red line = cut path (backing stays intact)
                        </text>
                      )}
                    </svg>
                  </div>
                ) : (
                  <div className="bg-stock border-2 border-ink flex items-center justify-center" style={{ height: "clamp(180px, 28vh, 280px)" }} data-testid="large-format-preview">
                    <svg viewBox="0 0 100 80" preserveAspectRatio="xMidYMid meet" className="h-full max-h-[250px]">
                      <rect x="5" y="5" width="90" height="70" rx="0" fill="#FBF8F1" stroke="#0A0A0A" strokeWidth="0.8" />
                      {artworkPreview && <image href={artworkPreview} x="10" y="10" width="80" height="60" preserveAspectRatio="xMidYMid meet" />}
                      {!artworkPreview && <text x="50" y="42" textAnchor="middle" fill="rgba(0,0,0,0.15)" fontSize="4">Your Design</text>}
                    </svg>
                  </div>
                )}
                <div className="text-center mt-2">
                  <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-muted">
                    {category === "apparel" ? `${gInfo.name} — ${color}` : category === "stickers" ? `${STICKER_CUT_TYPES.find(c => c.id === stickerCutType)?.label} — ${STICKER_SHAPES.find(s => s.id === stickerShape)?.label} — ${stickerWidth}" x ${stickerHeight}"` : `${lfSelected?.name || "Product"} — ${lfSize}`}
                  </p>
                </div>
              </div>
            </div>

            {/* Price + Actions (always visible) */}
            {category === "apparel" && (
              <div className="flex gap-0 mt-3 border-2 border-ink" data-testid="rush-tier-selector">
                {[
                  { id: "standard", label: "Standard", desc: "10-14 days", extra: "" },
                  { id: "rush", label: "Rush", desc: "5-7 days", extra: "+25%" },
                  { id: "emergency", label: "Emergency", desc: "2-3 days", extra: "+50%" },
                ].map((t, i) => (
                  <button
                    key={t.id}
                    onClick={() => setRushTier(t.id)}
                    className={`flex-1 py-1.5 px-1 text-center transition-all ${i > 0 ? "border-l-2 border-ink" : ""} ${
                      rushTier === t.id
                        ? "bg-ink text-acid" : "bg-bone text-ink hover:bg-stock"
                    }`}
                    data-testid={`rush-${t.id}`}
                  >
                    <span className="block font-mono text-[10px] uppercase tracking-[0.08em] font-bold">{t.label}</span>
                    <span className="block font-mono text-[9px] text-inherit opacity-60">{t.desc}</span>
                    {t.extra && <span className="block font-mono text-[9px] font-bold text-signal">{t.extra}</span>}
                  </button>
                ))}
              </div>
            )}
            <div className="border-2 border-ink mt-3 bg-acid" style={{ boxShadow: "6px 6px 0px 0px #FF2D1A" }}>
              <div className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-ink/60">{category === "stickers" ? `Est. (${quantity} stickers)` : category === "apparel" ? `Est. (${quantity} pcs)` : "Starting"}</p>
                    <p className="font-archivo text-2xl uppercase text-ink" data-testid="price-display">{priceInfo.total}</p>
                    <p className="font-mono text-[10px] text-ink/60">{priceInfo.per}</p>
                    {price?.rush_fee > 0 && (
                      <p className="font-mono text-[10px] text-signal font-bold mt-0.5" data-testid="rush-fee-label">
                        Includes ${price.rush_fee.toFixed(2)} {price.rush_label} fee
                      </p>
                    )}
                  </div>
                  {designNumber && <span className="bg-ink text-acid font-mono text-[10px] uppercase tracking-wider px-2 py-1 border-2 border-ink" data-testid="design-number-badge">{designNumber}</span>}
                </div>
                <div className="grid grid-cols-2 gap-0">
                  <Button onClick={handleBuyNow} className="bg-signal text-bone h-10 font-mono text-[11px] uppercase tracking-wider font-bold border-2 border-ink rounded-none hover:bg-ink hover:text-acid" data-testid="buy-now-btn">
                    <ShoppingCart className="w-3.5 h-3.5 mr-1" /> Add to Cart
                  </Button>
                  <Button onClick={handleQuote} className="bg-bone text-ink h-10 font-mono text-[11px] uppercase tracking-wider font-bold border-2 border-ink border-l-0 rounded-none hover:bg-cobalt hover:text-bone" data-testid="get-quote-btn">
                    <FileText className="w-3.5 h-3.5 mr-1" /> Get Quote
                  </Button>
                </div>
                <Button
                  onClick={handleShareProof}
                  disabled={creatingProof}
                  className="w-full mt-2 h-8 font-mono text-[10px] uppercase tracking-wider font-bold bg-ink text-acid border-2 border-ink rounded-none hover:bg-cobalt hover:text-bone gap-1"
                  data-testid="share-proof-btn"
                >
                  {proofCopied ? <Check className="w-3.5 h-3.5" /> : <Share2 className="w-3.5 h-3.5" />}
                  {creatingProof ? "Creating..." : proofCopied ? "Link Copied!" : proofId ? "Copy Proof Link" : "Share Proof"}
                </Button>
                {proofId && (
                  <p className="font-mono text-[10px] text-center text-ink/50 mt-1" data-testid="proof-id-label">
                    Proof: <a href={`/proof/${proofId}`} className="text-signal hover:underline" target="_blank" rel="noopener noreferrer">{proofId}</a>
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* ══ CONTROLS ══ */}
          <div className="lg:col-span-7 space-y-2 order-2">
            {category === "apparel" && (
              <>
                {/* 1. Garment */}
                <Card className="border-0 shadow-sm">
                  <CardContent className="p-3">
                    <h3 className="text-xs font-bold text-gray-800 mb-2">1. Garment</h3>
                    <div className="grid grid-cols-4 sm:grid-cols-7 gap-1.5">
                      {Object.entries(GARMENT_TYPES).map(([id, g]) => (
                        <button key={id} onClick={() => setGarment(id)}
                          className={`p-1.5 rounded-lg border text-center transition ${garment === id ? "border-orange-500 bg-orange-50 text-orange-700" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}
                          data-testid={`garment-${id}`}>
                          <span className="block text-[11px] font-medium leading-tight">{g.name}</span>
                          <span className="block text-[9px] text-gray-400">${g.price}</span>
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* 2. Print Method */}
                <Card className="border-0 shadow-sm">
                  <CardContent className="p-3">
                    <h3 className="text-xs font-bold text-gray-800 mb-2">2. Print Method</h3>
                    <div className="grid grid-cols-3 gap-1.5">
                      {PRINT_METHODS.map(m => (
                        <button key={m.id} onClick={() => setPrintMethod(m.id)}
                          className={`p-2 rounded-lg border text-center transition ${printMethod === m.id ? "border-orange-500 bg-orange-50" : "border-gray-200 hover:border-gray-300"}`}
                          data-testid={`method-${m.id}`}>
                          {m.id === "embroidery" && <Scissors className={`w-4 h-4 mx-auto mb-0.5 ${printMethod === m.id ? "text-orange-600" : "text-gray-400"}`} />}
                          {m.id === "dtf_printing" && <Palette className={`w-4 h-4 mx-auto mb-0.5 ${printMethod === m.id ? "text-orange-600" : "text-gray-400"}`} />}
                          {m.id === "screen_printing" && <Palette className={`w-4 h-4 mx-auto mb-0.5 ${printMethod === m.id ? "text-orange-600" : "text-gray-400"}`} />}
                          <span className={`block text-[11px] font-semibold ${printMethod === m.id ? "text-orange-700" : "text-gray-700"}`}>{m.label}</span>
                          <span className="block text-[9px] text-gray-400">{m.desc}</span>
                        </button>
                      ))}
                    </div>

                    {/* Embroidery extras */}
                    {printMethod === "embroidery" && (
                      <div className="mt-3 bg-orange-50 rounded-lg p-3 space-y-2">
                        <div className="flex items-center gap-2 mb-1">
                          <Ruler className="w-4 h-4 text-orange-600" />
                          <span className="text-xs font-bold text-orange-800">Embroidery Size & Estimate</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-[10px] text-orange-700">Width (inches)</Label>
                            <Input type="number" step="0.5" min="0.5" max="15" value={embWidth}
                              onChange={e => setEmbWidth(parseFloat(e.target.value) || 0)}
                              className="h-8 text-sm mt-0.5" data-testid="emb-width" />
                          </div>
                          <div>
                            <Label className="text-[10px] text-orange-700">Height (inches)</Label>
                            <Input type="number" step="0.5" min="0.5" max="15" value={embHeight}
                              onChange={e => setEmbHeight(parseFloat(e.target.value) || 0)}
                              className="h-8 text-sm mt-0.5" data-testid="emb-height" />
                          </div>
                        </div>
                        {embEstimate && (
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 mt-1" data-testid="emb-estimate">
                            <div className="bg-white rounded-md p-2 text-center">
                              <p className="text-sm font-bold text-gray-900">{embEstimate.estimated_stitches.toLocaleString()}</p>
                              <p className="text-[9px] text-gray-500">Stitches</p>
                            </div>
                            <div className="bg-white rounded-md p-2 text-center">
                              <p className="text-sm font-bold text-gray-900">{embEstimate.estimated_colors}</p>
                              <p className="text-[9px] text-gray-500">Colors</p>
                            </div>
                            <div className="bg-white rounded-md p-2 text-center">
                              <p className="text-sm font-bold text-gray-900">{embEstimate.estimated_time_minutes}m</p>
                              <p className="text-[9px] text-gray-500">Per Piece</p>
                            </div>
                            <div className="bg-white rounded-md p-2 text-center">
                              <p className="text-sm font-bold text-orange-600">{embEstimate.size_tier}</p>
                              <p className="text-[9px] text-gray-500">Size Tier</p>
                            </div>
                          </div>
                        )}
                        <p className="text-[9px] text-orange-600 leading-snug">Estimates based on 1,000 stitches per sq. inch. Colors (up to 15 max) do NOT affect pricing. Actual count determined during digitizing.</p>
                      </div>
                    )}

                    {/* Screen printing colors */}
                    {printMethod === "screen_printing" && (
                      <div className="mt-3 flex items-center gap-3">
                        <Label className="text-xs text-gray-600">Colors:</Label>
                        <div className="flex items-center gap-1">
                          <button onClick={() => setScreenColors(c => Math.max(1, c-1))} className="p-1 rounded bg-gray-100 hover:bg-gray-200"><Minus className="w-3 h-3" /></button>
                          <span className="w-8 text-center text-sm font-medium">{screenColors}</span>
                          <button onClick={() => setScreenColors(c => Math.min(12, c+1))} className="p-1 rounded bg-gray-100 hover:bg-gray-200"><Plus className="w-3 h-3" /></button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* 3. Color + Placement row */}
                <div className="grid sm:grid-cols-2 gap-2">
                  <Card className="border-0 shadow-sm">
                    <CardContent className="p-3">
                      <h3 className="text-xs font-bold text-gray-800 mb-2">3. Color</h3>
                      <div className="flex flex-wrap gap-1.5">
                        {Object.entries(COLORS).map(([name, hex]) => (
                          <button key={name} onClick={() => setColor(name)} title={name}
                            className={`w-7 h-7 rounded-full border-2 transition ${color === name ? "border-orange-500 scale-110 shadow" : "border-gray-200"}`}
                            style={{ backgroundColor: hex }} data-testid={`color-${name.toLowerCase().replace(/\s/g,"-")}`} />
                        ))}
                      </div>
                      <p className="text-[10px] text-gray-400 mt-1">{color}</p>
                    </CardContent>
                  </Card>
                  <Card className="border-0 shadow-sm">
                    <CardContent className="p-3">
                      <h3 className="text-xs font-bold text-gray-800 mb-2">4. Placement</h3>
                      <div className="grid grid-cols-2 gap-1.5">
                        {PLACEMENTS.map(p => (
                          <button key={p.id} onClick={() => setPlacement(p.id)}
                            className={`p-1.5 rounded-lg border text-[11px] font-medium transition ${placement === p.id ? "border-orange-500 bg-orange-50 text-orange-700" : "border-gray-200 text-gray-600"}`}
                            data-testid={`placement-${p.id}`}>
                            {p.label}
                          </button>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}

            {/* ── STICKERS controls ── */}
            {category === "stickers" && (
              <>
                {/* 1. Cut Type */}
                <Card className="border-0 shadow-sm">
                  <CardContent className="p-3">
                    <h3 className="text-xs font-bold text-gray-800 mb-2">1. Cut Type</h3>
                    <div className="grid grid-cols-2 gap-2">
                      {STICKER_CUT_TYPES.map(ct => (
                        <button key={ct.id} onClick={() => setStickerCutType(ct.id)}
                          className={`p-3 rounded-lg border text-center transition ${stickerCutType === ct.id ? "border-orange-500 bg-orange-50" : "border-gray-200 hover:border-gray-300"}`}
                          data-testid={`sticker-cut-${ct.id}`}>
                          <svg viewBox="0 0 100 100" className="w-10 h-10 mx-auto mb-1">
                            <path d={ct.icon} fill={stickerCutType === ct.id ? "#f97316" : "#d1d5db"} stroke={stickerCutType === ct.id ? "#ea580c" : "#9ca3af"} strokeWidth="1.5" />
                          </svg>
                          <span className={`block text-[11px] font-semibold ${stickerCutType === ct.id ? "text-orange-700" : "text-gray-700"}`}>{ct.label}</span>
                          <span className="block text-[9px] text-gray-400">{ct.desc}</span>
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* 2. Shape */}
                <Card className="border-0 shadow-sm">
                  <CardContent className="p-3">
                    <h3 className="text-xs font-bold text-gray-800 mb-2">2. Shape</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                      {STICKER_SHAPES.map(sh => (
                        <button key={sh.id} onClick={() => { setStickerShape(sh.id); if (sh.id === "round") { setStickerHeight(stickerWidth); } }}
                          className={`p-2 rounded-lg border text-center transition ${stickerShape === sh.id ? "border-orange-500 bg-orange-50 text-orange-700" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}
                          data-testid={`sticker-shape-${sh.id}`}>
                          <span className="block text-[11px] font-medium leading-tight">{sh.label}</span>
                          <span className="block text-[9px] text-gray-400">{sh.desc}</span>
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* 3. Size */}
                <Card className="border-0 shadow-sm">
                  <CardContent className="p-3">
                    <h3 className="text-xs font-bold text-gray-800 mb-2">3. Size</h3>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[10px] text-gray-500">{stickerShape === "round" ? "Diameter (inches)" : "Width (inches)"}</Label>
                        <Input type="number" step="0.5" min="1" max="12" value={stickerWidth}
                          onChange={e => { const v = parseFloat(e.target.value) || 1; setStickerWidth(v); if (stickerShape === "round") setStickerHeight(v); }}
                          className="h-8 text-sm mt-0.5" data-testid="sticker-width" />
                      </div>
                      {stickerShape !== "round" && (
                        <div>
                          <Label className="text-[10px] text-gray-500">Height (inches)</Label>
                          <Input type="number" step="0.5" min="1" max="12" value={stickerHeight}
                            onChange={e => setStickerHeight(parseFloat(e.target.value) || 1)}
                            className="h-8 text-sm mt-0.5" data-testid="sticker-height" />
                        </div>
                      )}
                    </div>
                    <p className="text-[9px] text-gray-400 mt-1">Min order: 25 stickers. Full-color, waterproof vinyl.</p>
                  </CardContent>
                </Card>

                {/* 4. Quantity */}
                <Card className="border-0 shadow-sm">
                  <CardContent className="p-3">
                    <h3 className="text-xs font-bold text-gray-800 mb-2">4. Quantity</h3>
                    <div className="flex items-center border rounded-md h-8 max-w-[200px]">
                      <button className="px-2 hover:bg-gray-100 h-full" onClick={() => setQuantity(q => Math.max(25, q - 25))}><Minus className="w-3 h-3" /></button>
                      <input type="number" value={quantity} onChange={e => setQuantity(Math.max(25, parseInt(e.target.value) || 25))} className="w-full text-center text-sm border-0 focus:ring-0 p-0" data-testid="sticker-quantity" />
                      <button className="px-2 hover:bg-gray-100 h-full" onClick={() => setQuantity(q => q + 25)}><Plus className="w-3 h-3" /></button>
                    </div>
                  </CardContent>
                </Card>

                {/* Bulk Order Discount Banner */}
                <div className="rounded-lg border border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 p-3" data-testid="bulk-discount-banner">
                  <p className="text-xs font-bold text-green-800 mb-1.5">Bulk Order Savings</p>
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5">
                    {[
                      { qty: 100, pct: "30%" },
                      { qty: 250, pct: "40%" },
                      { qty: 500, pct: "50%" },
                      { qty: 1000, pct: "60%" },
                      { qty: 2500, pct: "65%" },
                    ].map(tier => (
                      <button key={tier.qty} onClick={() => setQuantity(tier.qty)}
                        className={`p-1.5 rounded-md text-center border transition ${quantity >= tier.qty ? "bg-green-600 text-white border-green-600" : "bg-white border-green-200 hover:border-green-400"}`}
                        data-testid={`bulk-${tier.qty}`}>
                        <span className={`block text-[11px] font-bold ${quantity >= tier.qty ? "text-white" : "text-green-700"}`}>{tier.qty}+</span>
                        <span className={`block text-[9px] font-semibold ${quantity >= tier.qty ? "text-green-100" : "text-green-600"}`}>Save {tier.pct}</span>
                      </button>
                    ))}
                  </div>
                  {quantity < 100 && (
                    <p className="text-[10px] text-green-600 mt-1.5">Order 100+ stickers and save up to 65% per sticker!</p>
                  )}
                  {quantity >= 100 && quantity < 500 && (
                    <p className="text-[10px] text-green-600 mt-1.5">Great choice! Bump to 500+ for 50% off per sticker.</p>
                  )}
                  {quantity >= 500 && (
                    <p className="text-[10px] text-green-700 font-semibold mt-1.5">Maximum bulk savings unlocked!</p>
                  )}
                </div>
              </>
            )}


            {/* ── LARGE FORMAT controls ── */}
            {category === "large_format" && (
              <>
                <Card className="border-0 shadow-sm">
                  <CardContent className="p-3">
                    <h3 className="text-xs font-bold text-gray-800 mb-2">1. Product</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                      {lfProducts.map(p => (
                        <button key={p.id} onClick={() => { setLfSelected(p); setLfSize(p.sizes?.[0] || ""); setLfOptions({}); }}
                          className={`p-2 rounded-lg border text-left transition ${lfSelected?.id === p.id ? "border-orange-500 bg-orange-50" : "border-gray-200 hover:border-gray-300"}`}
                          data-testid={`lf-product-${p.id}`}>
                          <span className="block text-[11px] font-semibold text-gray-800 leading-tight">{p.name}</span>
                          <span className="block text-[9px] text-gray-400">${p.base_price} {p.price_unit}</span>
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
                {lfSelected && (
                  <Card className="border-0 shadow-sm">
                    <CardContent className="p-3 space-y-2">
                      <h3 className="text-xs font-bold text-gray-800">2. Size & Options</h3>
                      <p className="text-[11px] text-gray-500 leading-relaxed">{lfSelected.description}</p>
                      <div className="grid sm:grid-cols-2 gap-2">
                        <div>
                          <Label className="text-[10px] text-gray-500">Size</Label>
                          <Select value={lfSize} onValueChange={setLfSize}>
                            <SelectTrigger className="mt-0.5 h-8 text-sm"><SelectValue /></SelectTrigger>
                            <SelectContent>{lfSelected.sizes.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        {lfSelected.options && Object.entries(lfSelected.options).map(([key, vals]) => (
                          <div key={key}>
                            <Label className="text-[10px] text-gray-500 capitalize">{key}</Label>
                            <Select value={lfOptions[key] || ""} onValueChange={v => setLfOptions(prev => ({...prev, [key]: v}))}>
                              <SelectTrigger className="mt-0.5 h-8 text-sm"><SelectValue placeholder={`Select`} /></SelectTrigger>
                              <SelectContent>{vals.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                        ))}
                      </div>
                      <p className="text-[10px] text-gray-400">Turnaround: {lfSelected.turnaround}</p>
                    </CardContent>
                  </Card>
                )}
              </>
            )}

            {/* ── Upload + Order Details row ── */}
            <div className="grid sm:grid-cols-2 gap-2">
              <Card className="border-0 shadow-sm">
                <CardContent className="p-3">
                  <h3 className="text-xs font-bold text-gray-800 mb-2">{category === "apparel" ? "5" : category === "stickers" ? "5" : "3"}. Upload Design</h3>
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" />
                  <button onClick={() => fileInputRef.current?.click()}
                    className="w-full border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-orange-400 transition"
                    data-testid="upload-artwork-btn">
                    <Upload className="w-6 h-6 text-gray-400 mx-auto mb-1" />
                    <p className="text-xs font-medium text-gray-700">Tap to upload</p>
                    <p className="text-[9px] text-gray-400">PNG, JPG, SVG</p>
                  </button>
                  {artworkPreview && (
                    <div className="mt-2 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1">
                          <button onClick={() => setArtworkScale(s => Math.max(30, s-10))} className="p-0.5 rounded hover:bg-gray-100"><ZoomOut className="w-3.5 h-3.5 text-gray-500" /></button>
                          <span className="text-[10px] w-8 text-center">{artworkScale}%</span>
                          <button onClick={() => setArtworkScale(s => Math.min(150, s+10))} className="p-0.5 rounded hover:bg-gray-100"><ZoomIn className="w-3.5 h-3.5 text-gray-500" /></button>
                        </div>
                        <button onClick={() => { setArtwork(null); setArtworkPreview(null); setArtworkScale(100); setArtworkQuality(null); }} className="text-[10px] text-red-500 hover:text-red-700 flex items-center gap-0.5">
                          <RotateCcw className="w-3 h-3" /> Remove
                        </button>
                      </div>
                      {/* Quality badge + AI enhance */}
                      {artworkQuality && (
                        <div className={`rounded-lg p-2 text-[11px] ${
                          artworkQuality.status === "excellent" ? "bg-green-50 text-green-700 border border-green-200" :
                          artworkQuality.status === "needs_work" ? "bg-red-50 text-red-700 border border-red-200" :
                          "bg-amber-50 text-amber-700 border border-amber-200"
                        }`} data-testid="artwork-quality">
                          <p className="font-semibold">{artworkQuality.message}</p>
                          {(artworkQuality.status === "needs_work" || artworkQuality.status === "auto_vectorizable") && (
                            <Button
                              size="sm"
                              onClick={handleEnhanceArtwork}
                              disabled={enhancing}
                              className="mt-1.5 h-7 text-[10px] bg-gradient-to-r from-violet-600 to-indigo-600 text-white w-full"
                              data-testid="enhance-artwork-btn"
                            >
                              {enhancing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                              {enhancing ? "Enhancing (30-60s)..." : "Enhance with AI — Free"}
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="border-0 shadow-sm">
                <CardContent className="p-3 space-y-2">
                  <h3 className="text-xs font-bold text-gray-800">{category === "apparel" ? "6" : category === "stickers" ? "6" : "4"}. Details</h3>
                  {category === "apparel" && (
                    <div className="space-y-2" data-testid="size-breakdown">
                      {/* Size rows */}
                      {sizeBreakdown.map((row, i) => (
                        <div key={i} className="flex items-center gap-2" data-testid={`size-row-${i}`}>
                          <div className="flex-1">
                            {i === 0 && <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-muted block mb-0.5">Size</span>}
                            <Select value={row.size} onValueChange={(v) => {
                              setSizeBreakdown(prev => prev.map((r, idx) => idx === i ? { ...r, size: v } : r));
                            }}>
                              <SelectTrigger className="h-8 text-sm border-2 border-ink rounded-none bg-bone"><SelectValue /></SelectTrigger>
                              <SelectContent>{["XS","S","M","L","XL","2XL","3XL","4XL","5XL"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          <div className="w-24">
                            {i === 0 && <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-muted block mb-0.5">Qty</span>}
                            <div className="flex items-center border-2 border-ink h-8 bg-bone">
                              <button className="px-1.5 hover:bg-stock h-full" onClick={() => setSizeBreakdown(prev => prev.map((r, idx) => idx === i ? { ...r, qty: Math.max(1, r.qty - 1) } : r))}><Minus className="w-3 h-3" /></button>
                              <input
                                type="number"
                                value={row.qty}
                                onChange={(e) => setSizeBreakdown(prev => prev.map((r, idx) => idx === i ? { ...r, qty: Math.max(1, parseInt(e.target.value) || 1) } : r))}
                                className="w-full text-center text-sm border-0 focus:ring-0 p-0 bg-transparent font-mono"
                                data-testid={`size-qty-${i}`}
                              />
                              <button className="px-1.5 hover:bg-stock h-full" onClick={() => setSizeBreakdown(prev => prev.map((r, idx) => idx === i ? { ...r, qty: r.qty + 1 } : r))}><Plus className="w-3 h-3" /></button>
                            </div>
                          </div>
                          {sizeBreakdown.length > 1 && (
                            <button
                              onClick={() => setSizeBreakdown(prev => prev.filter((_, idx) => idx !== i))}
                              className="text-signal hover:text-ink mt-auto h-8 px-1"
                              data-testid={`remove-size-${i}`}
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      ))}

                      {/* Add size button */}
                      <button
                        onClick={() => {
                          const usedSizes = sizeBreakdown.map(r => r.size);
                          const allSizes = ["XS","S","M","L","XL","2XL","3XL","4XL","5XL"];
                          const next = allSizes.find(s => !usedSizes.includes(s)) || "M";
                          setSizeBreakdown(prev => [...prev, { size: next, qty: 1 }]);
                        }}
                        className="w-full py-1.5 border-2 border-dashed border-ink/30 font-mono text-[10px] uppercase tracking-[0.1em] text-muted hover:border-ink hover:text-ink hover:bg-stock transition-colors"
                        data-testid="add-size-btn"
                      >
                        + Add Size
                      </button>

                      {/* Total */}
                      <div className="flex items-center justify-between pt-1 border-t-2 border-ink/10">
                        <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-muted">Total Pieces</span>
                        <span className="font-archivo text-base text-ink" data-testid="total-pieces">
                          {sizeBreakdown.reduce((sum, r) => sum + (r.qty || 0), 0)}
                        </span>
                      </div>
                    </div>
                  )}
                  {category === "large_format" && (
                    <div>
                      <Label className="text-[10px] text-gray-500">Quantity</Label>
                      <div className="flex items-center mt-0.5 border rounded-md h-8">
                        <button className="px-1.5 hover:bg-gray-100 h-full" onClick={() => setQuantity(q => Math.max(1, q-1))}><Minus className="w-3 h-3" /></button>
                        <input type="number" value={quantity} onChange={e => setQuantity(Math.max(1, parseInt(e.target.value) || 1))} className="w-full text-center text-sm border-0 focus:ring-0 p-0" data-testid="quantity-input" />
                        <button className="px-1.5 hover:bg-gray-100 h-full" onClick={() => setQuantity(q => q+1)}><Plus className="w-3 h-3" /></button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* ── Design Services Upsell ── */}
            {artworkQuality && (artworkQuality.status === "needs_work" || artworkQuality.status === "auto_vectorizable") && (
              <Card className="border-0 shadow-md border-l-4 border-l-violet-500 bg-gradient-to-r from-violet-50 to-indigo-50" data-testid="design-services-upsell">
                <CardContent className="p-3">
                  <h3 className="text-xs font-bold text-gray-900 mb-2 flex items-center gap-1">
                    <Sparkles className="w-3.5 h-3.5 text-violet-600" /> Need help with your artwork?
                  </h3>
                  <div className="grid grid-cols-3 gap-2">
                    <button onClick={handleEnhanceArtwork} disabled={enhancing}
                      className="p-2 rounded-lg border border-violet-200 bg-white text-center hover:border-violet-400 transition"
                      data-testid="upsell-ai">
                      <p className="text-sm font-bold text-violet-700">Free</p>
                      <p className="text-[10px] font-semibold text-gray-800">AI Enhance</p>
                      <p className="text-[9px] text-gray-400">Auto-upscale</p>
                    </button>
                    <button onClick={() => navigate("/design-services")}
                      className="p-2 rounded-lg border border-orange-200 bg-white text-center hover:border-orange-400 transition"
                      data-testid="upsell-pro">
                      <p className="text-sm font-bold text-orange-600">$75</p>
                      <p className="text-[10px] font-semibold text-gray-800">Pro Review</p>
                      <p className="text-[9px] text-gray-400">Expert fixes</p>
                    </button>
                    <button onClick={() => navigate("/design-services")}
                      className="p-2 rounded-lg border border-red-200 bg-white text-center hover:border-red-400 transition"
                      data-testid="upsell-custom">
                      <p className="text-sm font-bold text-red-600">$150</p>
                      <p className="text-[10px] font-semibold text-gray-800">Custom Design</p>
                      <p className="text-[9px] text-gray-400">From scratch</p>
                    </button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* ── Notes / Tell us about your idea ── */}
            <Card className="border-0 shadow-sm">
              <CardContent className="p-3">
                <h3 className="text-xs font-bold text-gray-800 mb-1 flex items-center gap-1">
                  <MessageSquare className="w-3.5 h-3.5 text-orange-500" /> Tell us about your idea
                </h3>
                <p className="text-[10px] text-gray-400 mb-2">Describe what you're looking for — colors, vibe, deadline, questions. We'll follow up!</p>
                <Textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="e.g. I need 50 black tees with a 4-color logo on front and a name on back for a company event on March 15th..."
                  className="text-sm min-h-[60px]"
                  rows={3}
                  data-testid="design-notes"
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* ── Exit intent modal ── */}
      {showExitModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="exit-intent-modal">
          <Card className="max-w-sm w-full">
            <CardContent className="p-5 space-y-3">
              <div className="text-center">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-2"><Save className="w-6 h-6 text-orange-600" /></div>
                <h3 className="text-base font-bold">Wait! Save Your Design</h3>
                <p className="text-xs text-gray-500 mt-1">We'll save it so you can come back anytime.</p>
              </div>
              <Input placeholder="Name" value={exitForm.name} onChange={e => setExitForm({...exitForm, name: e.target.value})} data-testid="exit-name" />
              <Input placeholder="Email *" type="email" value={exitForm.email} onChange={e => setExitForm({...exitForm, email: e.target.value})} data-testid="exit-email" />
              <Input placeholder="Phone (optional)" value={exitForm.phone} onChange={e => setExitForm({...exitForm, phone: e.target.value})} data-testid="exit-phone" />
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" onClick={() => setShowExitModal(false)} data-testid="exit-dismiss-btn">No Thanks</Button>
                <Button onClick={handleExitCapture} className="bg-gradient-to-r from-orange-500 to-red-600 text-white" data-testid="exit-save-btn">Save</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── Chat slide-out ── */}
      {showChat && designNumber && (
        <ChatThread designNumber={designNumber} onClose={() => setShowChat(false)} />
      )}
      {showChat && !designNumber && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-4">
          <Card className="max-w-sm w-full">
            <CardContent className="p-5 text-center space-y-3">
              <MessageSquare className="w-8 h-8 text-gray-400 mx-auto" />
              <p className="text-sm text-gray-700 font-medium">Save your design first to start a conversation</p>
              <div className="flex gap-2 justify-center">
                <Button variant="outline" onClick={() => setShowChat(false)}>Cancel</Button>
                <Button onClick={async () => { await saveDesign(); }} className="bg-gradient-to-r from-orange-500 to-red-600 text-white">Save Design</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};


/* ═══════════ CHAT THREAD ═══════════ */

const ChatThread = ({ designNumber, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/designs/${designNumber}/messages`).then(r => {
      setMessages(r.data.messages || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [designNumber]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!text.trim()) return;
    try {
      const { data } = await axios.post(`${API}/designs/messages`, {
        design_number: designNumber, sender: "customer", sender_name: "Customer", message: text.trim(),
      });
      setMessages(prev => [...prev, data.data]);
      setText("");
    } catch { toast.error("Failed to send"); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-end z-50" onClick={onClose}>
      <div className="bg-white w-full sm:w-96 h-[70vh] sm:h-[500px] sm:m-4 sm:rounded-xl shadow-2xl flex flex-col" onClick={e => e.stopPropagation()} data-testid="chat-thread">
        <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
          <div>
            <h3 className="text-sm font-bold text-gray-900">Chat with Fit Print Bros</h3>
            <p className="text-[10px] text-gray-500">Design #{designNumber}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {loading && <p className="text-xs text-gray-400 text-center">Loading...</p>}
          {!loading && messages.length === 0 && (
            <div className="text-center py-8">
              <MessageSquare className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-xs text-gray-400">Start a conversation about your design.</p>
              <p className="text-[10px] text-gray-400 mt-1">Our team typically responds within a few hours.</p>
            </div>
          )}
          {messages.map(m => (
            <div key={m.id} className={`flex ${m.sender === "customer" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-xl px-3 py-2 ${m.sender === "customer" ? "bg-orange-500 text-white" : "bg-gray-100 text-gray-900"}`}>
                <p className="text-sm">{m.message}</p>
                <p className={`text-[9px] mt-0.5 ${m.sender === "customer" ? "text-orange-200" : "text-gray-400"}`}>
                  {m.sender_name} &middot; {new Date(m.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
          ))}
          <div ref={bottomRef} />
        </div>
        <div className="px-4 py-3 border-t flex gap-2 shrink-0">
          <Input value={text} onChange={e => setText(e.target.value)} placeholder="Type a message..."
            className="text-sm" onKeyDown={e => e.key === "Enter" && send()} data-testid="chat-input" />
          <Button onClick={send} className="bg-gradient-to-r from-orange-500 to-red-600 text-white shrink-0" data-testid="chat-send">
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export { DesignStudio };
export default DesignStudio;
